import { gql, useMutation } from '@apollo/client';
import { Num, Obj, Str } from '@livecontrol/core-utils';
import type { MutationResult } from '@livecontrol/scheduler/store';
import { useCallback, useState } from 'react';
import type { Installer } from '../../../model';
import * as Dbx from './dbx';

interface TData {
    upsertInstaller: Dbx.Record;
}

interface TVariables {
    input: {
        id?: number;
        url: string;
        notes?: string;
        name: string;
        expirationTime: number;
        app: string;
    };
}

type Args = Omit<TVariables['input'], 'app' | 'id'> & {
    id?: string;
    software: string;
};

const MUTATION = gql`
mutation UpsertInstaller($input: UpsertInstallerInput!) {
  upsertInstaller(input: $input) {
    url
    id
    expirationTime
    date_updated
    date_created
    app
  }
}
`;

export const useUpsertInstaller = (): [
    (args: Args) => Promise<Installer | undefined>,
    MutationResult<Installer, 'installer'>
] => {
    const [installer, setInstaller] = useState<Installer>();
    const [mutate, { loading, called }] = useMutation<TData, TVariables>(MUTATION);

    return [
        useCallback(async (args: Args) => {
            let installer_: Installer | undefined;

            try {
                const variables = {
                    url: Str.normalize(args.url),
                    name: Str.normalize(args.name),
                    expirationTime: Num.normalize(args.expirationTime),
                    app: Str.normalize(args.software)?.toLowerCase()
                };

                if (!Obj.isHydrated(variables)) {
                    return undefined;
                }

                installer_ = await mutate({
                    variables: {
                        input: {
                            ...variables,
                            id: Num.normalize(args.id),
                            notes: Str.normalize(args.notes)
                        }
                    }
                }).then(v => Dbx.normalize({
                    ...v.data?.upsertInstaller,
                    notes: args.notes,
                    name: args.name
                }));

            } catch {
                //
            }

            setInstaller(installer_);

            return installer_;
        }, [mutate]),
        {
            called,
            installer,
            loading
        }
    ];
};
